/**
 * Runtime Configuration Utility
 * 
 * This utility handles dynamic API URL configuration based on the runtime environment.
 * It supports both internal ECS service communication and external browser access.
 */

interface RuntimeConfig {
  apiUrl: string;
  isInternalEnvironment: boolean;
}

class RuntimeConfigManager {
  private static instance: RuntimeConfigManager;
  private config: RuntimeConfig | null = null;
  private isRuntimeConfigEnabled: boolean;

  private constructor() {
    this.isRuntimeConfigEnabled = process.env.REACT_APP_ENABLE_RUNTIME_CONFIG === 'true';
  }

  public static getInstance(): RuntimeConfigManager {
    if (!RuntimeConfigManager.instance) {
      RuntimeConfigManager.instance = new RuntimeConfigManager();
    }
    return RuntimeConfigManager.instance;
  }

  /**
   * Detects if we're running in an internal ECS environment
   * Returns true for internal ECS, false for browser/external access
   */
  private detectEnvironment(): boolean {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      // Server-side rendering or Node.js environment
      return true;
    }

    // Check if we're accessing via localhost (development)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return false;
    }

    // Check if we're accessing via an internal ECS service name
    const hostname = window.location.hostname;
    
    // Internal ECS service names are simple hostnames without dots (like 'backend', 'frontend')
    // OR end with .local for local development
    // CloudFront domains (like d2ryn4t3odd9cr.cloudfront.net) and ALB domains have dots but are external
    if (!hostname.includes('.') || hostname.endsWith('.local')) {
      return true;
    }
    
    // Special case: if hostname has dots but is NOT a .local domain, it's external (CloudFront/ALB)
    if (hostname.includes('.') && !hostname.endsWith('.local')) {
      return false;
    }

    // If we're accessing via CloudFront, ALB, or any public domain, we're in browser environment
    return false;
  }

  /**
   * Builds the appropriate API URL based on environment detection
   */
  private buildApiUrl(): string {
    const isInternal = this.detectEnvironment();

    if (!this.isRuntimeConfigEnabled) {
      // Fall back to static environment variable
      return process.env.REACT_APP_API_URL || 'http://localhost:8000';
    }

    // Special case: localhost development
    if (typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      // For local development, explicitly point to backend on port 8000
      return process.env.REACT_APP_API_URL || 'http://localhost:8000';
    }

    if (isInternal) {
      // Use internal ECS service name for service-to-service communication
      const backendService = process.env.REACT_APP_BACKEND_SERVICE || 'backend';
      return `http://${backendService}:8000`;
    } else {
      // Use empty string for browser requests - API endpoints already include /api prefix
      // This allows the browser to use the same domain/ALB that served the frontend
      return '';
    }
  }


  /**
   * Gets the runtime configuration, computing it if necessary
   */
  public getConfig(): RuntimeConfig {
    if (!this.config) {
      const isInternal = this.detectEnvironment();
      this.config = {
        apiUrl: this.buildApiUrl(),
        isInternalEnvironment: isInternal
      };

      console.log('Runtime Config:', {
        isRuntimeConfigEnabled: this.isRuntimeConfigEnabled,
        isInternalEnvironment: isInternal,
        apiUrl: this.config.apiUrl,
        hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A'
      });
    }

    return this.config;
  }

  /**
   * Forces a recalculation of the configuration
   * Useful for testing or when environment changes
   */
  public refreshConfig(): RuntimeConfig {
    this.config = null;
    return this.getConfig();
  }

  /**
   * Gets just the API URL
   */
  public getApiUrl(): string {
    return this.getConfig().apiUrl;
  }

  /**
   * Checks if we're in an internal environment
   */
  public isInternal(): boolean {
    return this.getConfig().isInternalEnvironment;
  }
}

// Export singleton instance
export const runtimeConfig = RuntimeConfigManager.getInstance();

// Export types for use in other files
export type { RuntimeConfig };

// Export convenience functions
export const getApiUrl = () => runtimeConfig.getApiUrl();
export const isInternalEnvironment = () => runtimeConfig.isInternal();